import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../../connector/api";
import toast, { Toaster } from "react-hot-toast";
import {
  ArrowLeft,
  Edit2,
  Trash2,
  Calendar,
  FileText,
  Layers,
  User,
  BookOpen,
  Bookmark,
  Camera,
  ChevronLeft,
  ChevronRight,
  Search,
  Info,
} from "lucide-react";
import AppLayout from "../components/common/AppLagout";
import { CustomSelect } from "../components/common/CustomSelect";
import NewBookModal from "../components/NewBookModal";
import ReturnModal from "../components/ReturnModal";

const CATEGORY_CHOICES = [
  "Adult Fiction",
  "Adult Non-fiction",
  "Ellen G. White",
  "Board Books",
  "DVD's",
  "Juvenile Non-fiction",
  "Young Adult",
  "Christmas Books",
  "Khmer",
  "Reference",
  "Story Collection",
  "Level Books",
  "Teacher Resources",
  "Biography",
  "Other",
];

const STATUS_CHOICES = ["available", "borrowed", "lost", "removed"];

const STATUS_COLORS = {
  available: "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20",
  borrowed: "bg-amber-500/10 text-amber-400 border border-amber-500/20",
  lost: "bg-rose-500/10 text-rose-400 border border-rose-500/20",
  removed: "bg-stone-800 text-stone-400 border border-stone-700",
};

const INITIAL_FORM_DATA = {
  title: "",
  author: "",
  category: "",
  isbn: "",
  barcode: "",
  description: "",
  call_number: "",
  publication_year: "",
  publisher: "",
  status: "available",
};

export default function ViewBook() {
  const { id } = useParams();
  const navigate = useNavigate();

  // Header / Global Search state
  const [headerSearchTerm, setHeaderSearchTerm] = useState("");
  const [headerSearchLoading, setHeaderSearchLoading] = useState(false);
  const [headerSearchResults, setHeaderSearchResults] = useState([]);
  const [headerShowDropdown, setHeaderShowDropdown] = useState(false);
  const [headerHighlightedIndex, setHeaderHighlightedIndex] = useState(-1);
  const searchRef = useRef(null);

  // Book State & Modals
  const [book, setBook] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);

  // Loan History State & Pagination
  const [loanHistory, setLoanHistory] = useState([]);
  const [loadingLoans, setLoadingLoans] = useState(false);
  const [showCurrentOnly, setShowCurrentOnly] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [returnLoan, setReturnLoan] = useState(null);
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);

  // Formatting Helpers
  const getCoverUrl = (path) => {
    if (!path) return null;
    if (path.startsWith("http://") || path.startsWith("https://")) return path;
    const base = api.defaults.baseURL ? api.defaults.baseURL.replace(/\/api\/?$/, "") : "";
    const normalizedPath = path.startsWith("/") ? path : `/${path}`;
    return `${base}${normalizedPath}`;
  };

  const formatMatrixDate = (dateInput) => {
    if (!dateInput) return "—";
    const dateObj = new Date(dateInput);
    if (isNaN(dateObj.getTime())) return "—";
    return dateObj.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Debounce Page Search
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 400);

    return () => clearTimeout(handler);
  }, [searchTerm]);

  // Header Search Effect
  useEffect(() => {
    if (!headerSearchTerm.trim()) {
      setHeaderSearchResults([]);
      setHeaderShowDropdown(false);
      return;
    }

    setHeaderSearchLoading(true);
    const handler = setTimeout(async () => {
      try {
        const res = await api.get("/cataloging/dashboard/search", {
          params: { q: headerSearchTerm },
        });
        const data = res.data || [];
        const normalizedResults = data.map((b) => ({
          ...b,
          cover_url: getCoverUrl(b.cover_image),
          is_available: b.status === "available",
        }));
        setHeaderSearchResults(normalizedResults);
        setHeaderShowDropdown(true);
      } catch (err) {
        console.error("Header search failed:", err);
        setHeaderSearchResults([]);
      } finally {
        setHeaderSearchLoading(false);
      }
    }, 400);

    return () => clearTimeout(handler);
  }, [headerSearchTerm]);

  const handleSelectBook = (selectedBook) => {
    const bookId = selectedBook?.id || selectedBook?._id;

    if (!bookId) {
      console.error("Selected book missing valid ID:", selectedBook);
      return;
    }

    setHeaderShowDropdown(false);
    setHeaderSearchTerm("");
    
    // ✅ FIXED: Added /book/detail/ to match your React Router route
    navigate(`/library/cataloging/book/detail/${bookId}`); 
  };

  // Data Fetching
  useEffect(() => {
    setBook(null); // Reset state so loading spinner triggers on ID change
    const fetchBook = async () => {
      try {
        const res = await api.get(`/cataloging/${id}/`);
        const data = res.data;
        data.cover_image = getCoverUrl(data.cover_image);
        setBook(data);
        setFormData(data);
      } catch {
        toast.error("Failed to load book");
      }
    };
    if (id) fetchBook();
  }, [id]);

  const fetchLoanHistory = useCallback(async () => {
    try {
      setLoadingLoans(true);
      let url = `/cataloging/${id}/loan_history/?page=${currentPage}&page_size=${pageSize}`;
      if (showCurrentOnly) url += "&current_only=true";

      const res = await api.get(url);
      setLoanHistory(res.data.results || []);
      setTotalPages(Math.ceil((res.data.count || 0) / pageSize));
    } catch {
      toast.error("Failed to load loan history");
    } finally {
      setLoadingLoans(false);
    }
  }, [id, currentPage, pageSize, showCurrentOnly]);

  useEffect(() => {
    if (id) fetchLoanHistory();
  }, [id, fetchLoanHistory]);

  // Form Operations
  const handleUpdateBook = async (e) => {
    e.preventDefault();
    try {
      const res = await api.put(`/cataloging/${id}/`, formData);
      toast.success("Book updated successfully");
      const coverUrl = getCoverUrl(res.data.cover_image);
      setBook((prev) => ({ ...prev, ...res.data, cover_image: coverUrl }));
      setModalOpen(false);
    } catch {
      toast.error("Failed to update book");
    }
  };

  const handleDeleteBook = async () => {
    try {
      await api.delete(`/cataloging/${id}/`);
      toast.success("Book deleted successfully");
      setTimeout(() => navigate("/library/cataloging", { replace: true }), 400);
    } catch {
      toast.error("Failed to delete book");
    }
  };

  const handleCoverUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const fd = new FormData();
    fd.append("image", file);

    try {
      setUploadingCover(true);
      const res = await api.post(`/cataloging/${id}/upload-cover/`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const fullUrl = getCoverUrl(res.data.cover_image);
      setBook((prev) => ({ ...prev, cover_image: fullUrl }));
      toast.success("Cover uploaded successfully");
    } catch {
      toast.error("Failed to upload cover");
    } finally {
      setUploadingCover(false);
    }
  };

  const handleRemoveCover = async () => {
    try {
      await api.post(`/cataloging/${id}/remove-cover/`);
      setBook((prev) => ({ ...prev, cover_image: null }));
      toast.success("Cover removed");
    } catch {
      toast.error("Failed to clear cover");
    }
  };

  const headerProps = {
    title: book?.title ? `Catalog / ${book.title}` : "Catalog Details",
    searchTerm: headerSearchTerm,
    setSearchTerm: setHeaderSearchTerm,
    searchRef,
    searchLoading: headerSearchLoading,
    searchResults: headerSearchResults,
    showDropdown: headerShowDropdown,
    setShowDropdown: setHeaderShowDropdown,
    highlightedIndex: headerHighlightedIndex,
    setHighlightedIndex: setHeaderHighlightedIndex,
    handleSelectBook,
  };

  if (!book) {
    return (
      <AppLayout headerProps={headerProps}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-pulse flex flex-col items-center gap-3">
            <div className="h-6 w-6 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
            <p className="text-xs text-stone-400 font-medium">Loading catalog records...</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  // Derived filtered loan history (Fixed: Uses debouncedSearch & includes end of day for toDate)
  const filteredLoanHistory = loanHistory.filter((loan) => {
    const matchesSearch = (loan.borrower || "")
      .toLowerCase()
      .includes(debouncedSearch.toLowerCase());
    const matchesFrom = !fromDate || new Date(loan.borrow_date) >= new Date(fromDate);
    const matchesTo =
      !toDate || new Date(loan.borrow_date) <= new Date(`${toDate}T23:59:59`);
    return matchesSearch && matchesFrom && matchesTo;
  });

  return (
    <AppLayout headerProps={headerProps}>
      <Toaster position="top-right" />
      <div className="space-y-6">
        {/* Top Level Breadcrumb Navigation */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-5 border-b border-[#2e2720]">
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2 text-[10px] font-sans font-semibold tracking-wider text-stone-500 uppercase">
              <span
                className="hover:text-amber-400 cursor-pointer transition-colors"
                onClick={() => navigate("/library/cataloging")}
              >
                Cataloging
              </span>
              <span>/</span>
              <span className="text-stone-300">Resource Dashboard</span>
            </div>
            <h1 className="font-serif text-lg font-bold tracking-wider text-stone-100 mt-1">
              {book.title}
            </h1>
          </div>

          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center justify-center gap-2 px-4 py-2 text-xs font-medium text-stone-300 bg-[#1e1a17] border border-[#2e2720] rounded-xl hover:bg-[#25201a] hover:text-stone-100 transition active:scale-95 self-start sm:self-center"
          >
            <ArrowLeft className="h-4 w-4 text-amber-500" /> Back to Cataloging
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 items-start">
          {/* Cover Profile Display */}
          <div className="lg:col-span-1 flex flex-col items-center lg:items-stretch">
            <div className="relative w-full max-w-[240px] lg:max-w-none aspect-[3/4] rounded-2xl bg-[#131110] border border-[#2e2720] shadow-xl overflow-hidden group">
              {book.cover_image ? (
                <img
                  src={book.cover_image}
                  alt="Book cover art"
                  className="object-cover w-full h-full transform group-hover:scale-105 transition duration-500"
                />
              ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 p-4 text-center">
                  <div className="p-4 bg-[#201c17] rounded-2xl border border-[#2e2720]">
                    <Bookmark className="h-8 w-8 text-amber-500/60" />
                  </div>
                  <span className="text-xs font-semibold text-stone-500 uppercase tracking-wider">
                    No Cover Image
                  </span>
                </div>
              )}

              {/* Action Overlay */}
              <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent pt-12 p-4 flex flex-col gap-2 opacity-0 group-hover:opacity-100 transition duration-300">
                <label className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 text-xs font-bold bg-amber-500 hover:bg-amber-400 text-stone-950 rounded-xl shadow cursor-pointer transition active:scale-95">
                  <Camera className="w-3.5 h-3.5" />
                  {uploadingCover ? "Uploading..." : "Change Cover Art"}
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleCoverUpload}
                  />
                </label>
                {book.cover_image && (
                  <button
                    onClick={handleRemoveCover}
                    className="w-full inline-flex items-center justify-center px-3 py-2 text-xs font-bold bg-stone-900/80 backdrop-blur-md text-rose-400 rounded-xl border border-rose-500/20 hover:bg-rose-500/20 transition active:scale-95"
                  >
                    Remove
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Metadata Panel */}
          <div className="lg:col-span-3 space-y-6">
            <div className="bg-[#1a1613] rounded-2xl border border-[#2e2720] p-6 sm:p-8">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-6 border-b border-[#2e2720]">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 bg-amber-500/10 text-amber-400 rounded-xl border border-amber-500/20">
                    <Info className="w-5 h-5" />
                  </div>
                  <div>
                    <h2 className="text-sm font-bold text-stone-100">Resource Metadata</h2>
                    <p className="text-xs text-stone-500">
                      System attributes and classifications for internal catalog records.
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2 self-end sm:self-center">
                  <button
                    onClick={() => setModalOpen(true)}
                    className="p-2.5 bg-[#201c17] text-amber-400 border border-[#2e2720] hover:border-amber-500/40 rounded-xl transition active:scale-95"
                    title="Edit Record"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setDeleteModalOpen(true)}
                    className="p-2.5 bg-[#201c17] text-rose-400 border border-rose-500/20 hover:bg-rose-500/10 rounded-xl transition active:scale-95"
                    title="Delete Record"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Properties Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-5 text-sm pt-6">
                <div className="flex flex-col pb-3 border-b border-[#2e2720]">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-stone-500">
                    Primary Author
                  </span>
                  <span className="text-stone-200 font-semibold mt-1">
                    {book.author || "—"}
                  </span>
                </div>
                <div className="flex flex-col pb-3 border-b border-[#2e2720]">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-stone-500">
                    Category
                  </span>
                  <span className="text-stone-200 font-semibold mt-1">
                    {book.category || "—"}
                  </span>
                </div>
                <div className="flex flex-col pb-3 border-b border-[#2e2720]">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-stone-500">
                    ISBN Code
                  </span>
                  <span className="text-stone-300 font-mono font-medium mt-1">
                    {book.isbn || "—"}
                  </span>
                </div>
                <div className="flex flex-col pb-3 border-b border-[#2e2720]">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-stone-500">
                    Call Location Number
                  </span>
                  <span className="text-stone-300 font-mono font-medium mt-1">
                    {book.call_number || "—"}
                  </span>
                </div>
                <div className="flex flex-col pb-3 border-b border-[#2e2720]">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-stone-500">
                    Publication Year
                  </span>
                  <span className="text-stone-200 font-semibold mt-1">
                    {book.publication_year || "—"}
                  </span>
                </div>
                <div className="flex flex-col pb-3 border-b border-[#2e2720]">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-stone-500">
                    Barcode
                  </span>
                  <span className="text-stone-300 font-mono font-medium mt-1">
                    {book.barcode || "—"}
                  </span>
                </div>
                <div className="flex flex-col pb-3 border-b border-[#2e2720]">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-stone-500">
                    Publisher
                  </span>
                  <span className="text-stone-200 font-semibold mt-1">
                    {book.publisher || "—"}
                  </span>
                </div>
                <div className="flex flex-col pb-3 border-b border-[#2e2720]">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-stone-500 mb-1">
                    Book Status
                  </span>
                  <span
                    className={`inline-flex items-center w-fit rounded-full px-3 py-0.5 text-xs font-semibold uppercase tracking-wider ${
                      STATUS_COLORS[book.status] || "bg-stone-800 text-stone-400"
                    }`}
                  >
                    {book.status}
                  </span>
                </div>
              </div>

              {book.description && (
                <div className="mt-6 pt-5 border-t border-[#2e2720]">
                  <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-stone-500 mb-2">
                    <FileText className="w-3.5 h-3.5 text-amber-500" /> Book Synopsis & Annotation
                  </span>
                  <p className="text-xs text-stone-300 leading-relaxed bg-[#131110] rounded-xl p-4 border border-[#2e2720]">
                    {book.description}
                  </p>
                </div>
              )}

              {book.date_added && (
                <div className="mt-6 flex items-center gap-1.5 text-xs text-stone-500 font-medium">
                  <Calendar className="w-3.5 h-3.5" /> Entry established on{" "}
                  {new Date(book.date_added).toLocaleDateString(undefined, {
                    dateStyle: "long",
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Circulation History Section */}
        <div className="bg-[#1a1613] rounded-2xl border border-[#2e2720] overflow-hidden">
          <div className="p-6 border-b border-[#2e2720] bg-[#131110]/50 flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-amber-500/10 text-amber-400 rounded-xl border border-amber-500/20">
                <Layers className="h-5 w-5" />
              </div>
              <div>
                <h2 className="text-sm font-bold text-stone-100">Circulation History</h2>
                <p className="text-xs text-stone-500 mt-0.5">
                  Track distribution logs, active checkouts, and historical loan events.
                </p>
              </div>
            </div>

            {/* Filter Controls */}
            <div className="flex flex-wrap items-center gap-3">
              <CustomSelect
                value={showCurrentOnly ? "current" : "all"}
                onChange={(val) => {
                  setShowCurrentOnly(val === "current");
                  setCurrentPage(1);
                }}
                options={[
                  { value: "current", label: "Active Loans Only" },
                  { value: "all", label: "Full Circulation Archive" },
                ]}
              />

              <div className="relative w-full sm:w-52">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-stone-500" />
                <input
                  type="text"
                  placeholder="Search borrower..."
                  className="pl-9 pr-4 py-2 border border-[#2e2720] rounded-xl text-xs bg-[#131110] text-stone-200 placeholder-stone-600 focus:outline-none focus:border-amber-500/50 w-full"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                />
              </div>

              <div className="flex items-center gap-2 bg-[#131110] border border-[#2e2720] px-3 py-1.5 rounded-xl text-xs font-medium text-stone-400">
                <div className="flex items-center gap-1">
                  <span className="text-stone-500 font-bold uppercase text-[9px]">From</span>
                  <input
                    type="date"
                    className="bg-transparent border-none focus:outline-none p-0 text-xs text-stone-300 font-mono"
                    value={fromDate}
                    onChange={(e) => {
                      setFromDate(e.target.value);
                      setCurrentPage(1);
                    }}
                  />
                </div>
                <span className="text-stone-700">|</span>
                <div className="flex items-center gap-1">
                  <span className="text-stone-500 font-bold uppercase text-[9px]">To</span>
                  <input
                    type="date"
                    className="bg-transparent border-none focus:outline-none p-0 text-xs text-stone-300 font-mono"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Loan Table */}
          <div className="min-h-[250px]">
            {loadingLoans ? (
              <div className="p-16 text-center text-stone-500 flex flex-col items-center justify-center gap-2">
                <div className="h-5 w-5 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
                <span className="text-xs font-semibold mt-1">
                  Querying active transaction records...
                </span>
              </div>
            ) : filteredLoanHistory.length === 0 ? (
              <div className="p-16 text-center text-stone-500 flex flex-col items-center justify-center gap-3">
                <Layers className="h-8 w-8 text-stone-700" />
                <div>
                  <p className="text-sm font-bold text-stone-300">No Loan Logs Found</p>
                  <p className="text-xs text-stone-500 max-w-xs mx-auto mt-0.5">
                    No active or archived transactions match the applied parameters.
                  </p>
                </div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse table-auto">
                  <thead>
                    <tr className="bg-[#131110]/80 border-b border-[#2e2720] text-[10px] font-bold uppercase tracking-wider text-stone-500 select-none">
                      <th className="px-6 py-3.5">Borrower</th>
                      <th className="px-6 py-3.5">Checkout Date</th>
                      <th className="px-6 py-3.5">Due Date</th>
                      <th className="px-6 py-3.5">Returned Date</th>
                      <th className="px-6 py-3.5">Status</th>
                      <th className="px-6 py-3.5 text-right">Actions</th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-[#2e2720] text-xs">
                    {filteredLoanHistory.map((loan) => {
                      const todayStr = new Date().toISOString().split("T")[0];
                      const dueStr = loan.due_date
                        ? new Date(loan.due_date).toISOString().split("T")[0]
                        : "";
                      const hasReturned = Boolean(loan.returned_date);

                      let statusLabel = hasReturned ? "Returned" : "Active Borrow";
                      let badgeStyles = "bg-amber-500/10 text-amber-400 border-amber-500/20";
                      let dotStyles = "bg-amber-400";

                      if (!hasReturned) {
                        if (dueStr && dueStr < todayStr) {
                          statusLabel = "Overdue";
                          badgeStyles = "bg-rose-500/10 text-rose-400 border-rose-500/20";
                          dotStyles = "bg-rose-400 animate-pulse";
                        } else if (dueStr === todayStr) {
                          statusLabel = "Due Today";
                          badgeStyles = "bg-amber-500/10 text-amber-300 border-amber-500/30";
                          dotStyles = "bg-amber-300";
                        }
                      } else {
                        badgeStyles = "bg-emerald-500/10 text-emerald-400 border-emerald-500/20";
                        dotStyles = "bg-emerald-400";
                      }

                      return (
                        <tr key={loan.id} className="hover:bg-[#201c17]/60 transition-colors group">
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2.5 min-w-0">
                              <div className="p-1.5 bg-[#201c17] border border-[#2e2720] text-stone-400 rounded-md shrink-0">
                                <User className="h-3.5 w-3.5 text-amber-500" />
                              </div>
                              <div className="min-w-0">
                                <p className="font-bold text-stone-200 truncate">{loan.borrower}</p>
                                {loan.book_title && (
                                  <p className="text-[11px] text-stone-500 font-medium truncate max-w-xs flex items-center gap-1 mt-0.5">
                                    <BookOpen className="h-2.5 w-2.5 text-stone-500" />
                                    <span>{loan.book_title}</span>
                                  </p>
                                )}
                              </div>
                            </div>
                          </td>

                          <td className="px-6 py-4 text-stone-300 font-mono text-xs">
                            {formatMatrixDate(loan.borrow_date)}
                          </td>
                          <td className="px-6 py-4 text-stone-300 font-mono text-xs">
                            {formatMatrixDate(loan.due_date)}
                          </td>
                          <td className="px-6 py-4 text-xs">
                            {loan.returned_date ? (
                              <span className="text-stone-300 font-mono">
                                {formatMatrixDate(loan.returned_date)}
                              </span>
                            ) : (
                              <span className="text-[11px] font-semibold text-stone-500 italic bg-[#131110] border border-[#2e2720] px-2 py-0.5 rounded">
                                Outstanding
                              </span>
                            )}
                          </td>

                          <td className="px-6 py-4">
                            <span
                              className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 text-xs font-bold rounded-md border ${badgeStyles}`}
                            >
                              <span className={`h-1.5 w-1.5 rounded-full ${dotStyles}`} />
                              {statusLabel}
                            </span>
                          </td>

                          <td className="px-6 py-4 text-right">
                            {!loan.returned_date ? (
                              <button
                                type="button"
                                onClick={() => {
                                  if (!loan.id) {
                                    toast.error("Loan index corrupt, cannot return");
                                    return;
                                  }
                                  setReturnLoan({
                                    ...loan,
                                    book: { title: loan.book_title || "Unknown Asset" },
                                  });
                                }}
                                className="inline-flex items-center justify-center px-3 py-1.5 text-xs font-bold bg-[#201c17] text-amber-400 border border-amber-500/30 rounded-lg hover:bg-amber-500 hover:text-stone-950 transition active:scale-95"
                              >
                                Process Return
                              </button>
                            ) : (
                              <span className="text-[10px] font-bold tracking-wider text-stone-600 bg-[#131110] px-2 py-1 border border-[#2e2720] rounded-md select-none">
                                ARCHIVED
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                {/* Pagination Toolbar */}
                <div className="px-6 py-4 border-t border-[#2e2720] flex items-center justify-between bg-[#131110]/50 select-none">
                  <span className="text-xs font-semibold text-stone-500">
                    Page <strong className="text-stone-300">{currentPage}</strong> of{" "}
                    <strong className="text-stone-300">{totalPages || 1}</strong>
                  </span>

                  <div className="inline-flex items-center gap-1">
                    <button
                      type="button"
                      disabled={currentPage <= 1}
                      onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                      className="p-1.5 border border-[#2e2720] bg-[#1a1613] rounded-lg text-stone-400 hover:text-stone-100 disabled:opacity-40 transition"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      disabled={currentPage >= totalPages}
                      onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                      className="p-1.5 border border-[#2e2720] bg-[#1a1613] rounded-lg text-stone-400 hover:text-stone-100 disabled:opacity-40 transition"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Edit Resource Modal */}
      {modalOpen && (
        <NewBookModal onClose={() => setModalOpen(false)}>
          <div className="p-2 text-stone-200 flex flex-col">
            <form
              onSubmit={handleUpdateBook}
              className="space-y-4 text-xs overflow-y-auto pr-1.5 custom-scrollbar"
            >
              <div className="flex flex-col border border-[#2e2720] rounded-xl bg-[#131110] overflow-hidden focus-within:border-amber-500/50 transition">
                <div className="bg-[#1a1613] border-b border-[#2e2720] px-3 py-1.5">
                  <span className="font-bold text-stone-500 uppercase tracking-wider text-[10px]">
                    Resource Title
                  </span>
                </div>
                <div className="relative flex items-center">
                  <FileText className="absolute left-3 w-4 h-4 text-stone-500" />
                  <input
                    type="text"
                    className="w-full bg-transparent pl-10 pr-4 py-2.5 text-xs font-serif tracking-wider font-semibold text-stone-100 placeholder-stone-600 focus:outline-none"
                    placeholder="e.g., The Great Gatsby"
                    required
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex flex-col border border-[#2e2720] rounded-xl bg-[#131110] overflow-hidden focus-within:border-amber-500/50 transition">
                  <div className="bg-[#1a1613] border-b border-[#2e2720] px-3 py-1.5">
                    <span className="font-bold text-stone-500 uppercase tracking-wider text-[10px]">
                      Author
                    </span>
                  </div>
                  <input
                    type="text"
                    className="w-full bg-transparent px-3 py-2.5 text-xs font-semibold text-stone-100 placeholder-stone-600 focus:outline-none"
                    placeholder="Author surname, initials"
                    required
                    value={formData.author}
                    onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                  />
                </div>

                <div className="relative z-20 flex flex-col border border-[#2e2720] rounded-xl bg-[#131110] focus-within:border-amber-500/50 transition">
                  <div className="bg-[#1a1613] border-b border-[#2e2720] px-3 py-1.5 rounded-t-xl">
                    <span className="font-bold text-stone-500 uppercase tracking-wider text-[10px]">
                      Category
                    </span>
                  </div>
                  <div className="w-full p-1">
                    <CustomSelect
                      className="w-full bg-transparent border-none shadow-none focus:ring-0 text-xs font-semibold text-stone-100"
                      menuClassName="[&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-[#131110] [&::-webkit-scrollbar-thumb]:bg-[#2a241e] [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-amber-500"
                      value={formData.category}
                      onChange={(val) => setFormData({ ...formData, category: val })}
                      options={[
                        { value: "", label: "Choose Category..." },
                        ...CATEGORY_CHOICES.map((cat) => ({ value: cat, label: cat })),
                      ]}
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex flex-col border border-[#2e2720] rounded-xl bg-[#131110] overflow-hidden focus-within:border-amber-500/50 transition">
                  <div className="bg-[#1a1613] border-b border-[#2e2720] px-3 py-1.5">
                    <span className="font-bold text-stone-500 uppercase tracking-wider text-[10px]">
                      ISBN Standard
                    </span>
                  </div>
                  <input
                    type="text"
                    className="w-full bg-transparent px-3 py-2.5 text-xs font-semibold font-mono text-stone-100 placeholder-stone-600 focus:outline-none"
                    placeholder="ISBN Code"
                    required
                    value={formData.isbn}
                    onChange={(e) => setFormData({ ...formData, isbn: e.target.value })}
                  />
                </div>

                <div className="flex flex-col border border-[#2e2720] rounded-xl bg-[#131110] overflow-hidden focus-within:border-amber-500/50 transition">
                  <div className="bg-[#1a1613] border-b border-[#2e2720] px-3 py-1.5">
                    <span className="font-bold text-stone-500 uppercase tracking-wider text-[10px]">
                      Barcode Sequence
                    </span>
                  </div>
                  <input
                    type="text"
                    className="w-full bg-transparent px-3 py-2.5 text-xs font-semibold font-mono text-stone-100 placeholder-stone-600 focus:outline-none"
                    placeholder="Barcode Index"
                    value={formData.barcode}
                    onChange={(e) => setFormData({ ...formData, barcode: e.target.value })}
                  />
                </div>
              </div>

              <div className="flex flex-col border border-[#2e2720] rounded-xl bg-[#131110] overflow-hidden focus-within:border-amber-500/50 transition">
                <div className="bg-[#1a1613] border-b border-[#2e2720] px-3 py-1.5">
                  <span className="font-bold text-stone-500 uppercase tracking-wider text-[10px]">
                    Synopsis / Description
                  </span>
                </div>
                <textarea
                  className="w-full bg-transparent px-3 py-2.5 text-xs font-normal text-stone-300 placeholder-stone-600 focus:outline-none h-20 resize-none leading-relaxed custom-scrollbar"
                  placeholder="Input summary descriptions..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="flex flex-col border border-[#2e2720] rounded-xl bg-[#131110] overflow-hidden focus-within:border-amber-500/50 transition">
                  <div className="bg-[#1a1613] border-b border-[#2e2720] px-3 py-1.5">
                    <span className="font-bold text-stone-500 uppercase tracking-wider text-[10px]">
                      Call Number
                    </span>
                  </div>
                  <input
                    type="text"
                    className="w-full bg-transparent px-3 py-2.5 text-xs font-semibold text-stone-100 placeholder-stone-600 focus:outline-none"
                    placeholder="Shelf coordinate"
                    value={formData.call_number}
                    onChange={(e) => setFormData({ ...formData, call_number: e.target.value })}
                  />
                </div>

                <div className="flex flex-col border border-[#2e2720] rounded-xl bg-[#131110] overflow-hidden focus-within:border-amber-500/50 transition">
                  <div className="bg-[#1a1613] border-b border-[#2e2720] px-3 py-1.5">
                    <span className="font-bold text-stone-500 uppercase tracking-wider text-[10px]">
                      Year
                    </span>
                  </div>
                  <input
                    type="number"
                    className="w-full bg-transparent px-3 py-2.5 text-xs font-semibold text-stone-100 placeholder-stone-600 focus:outline-none"
                    placeholder="YYYY"
                    value={formData.publication_year}
                    onChange={(e) =>
                      setFormData({ ...formData, publication_year: e.target.value })
                    }
                  />
                </div>

                <div className="flex flex-col border border-[#2e2720] rounded-xl bg-[#131110] overflow-hidden focus-within:border-amber-500/50 transition">
                  <div className="bg-[#1a1613] border-b border-[#2e2720] px-3 py-1.5">
                    <span className="font-bold text-stone-500 uppercase tracking-wider text-[10px]">
                      Publisher
                    </span>
                  </div>
                  <input
                    type="text"
                    className="w-full bg-transparent px-3 py-2.5 text-xs font-semibold text-stone-100 placeholder-stone-600 focus:outline-none"
                    placeholder="Publisher entity"
                    value={formData.publisher}
                    onChange={(e) => setFormData({ ...formData, publisher: e.target.value })}
                  />
                </div>
              </div>

              <div className="relative z-10 flex flex-col border border-[#2e2720] rounded-xl bg-[#131110] focus-within:border-amber-500/50 transition">
                <div className="bg-[#1a1613] border-b border-[#2e2720] px-3 py-1.5 rounded-t-xl">
                  <span className="font-bold text-stone-500 uppercase tracking-wider text-[10px]">
                    Status
                  </span>
                </div>
                <div className="w-full p-1">
                  <CustomSelect
                    menuClassName="bottom-full top-auto mb-1.5 mt-0"
                    className="w-full bg-transparent border-none shadow-none focus:ring-0 text-xs font-semibold text-stone-100"
                    value={formData.status}
                    onChange={(val) => setFormData({ ...formData, status: val })}
                    options={STATUS_CHOICES.map((status) => ({
                      value: status,
                      label: status.toUpperCase(),
                    }))}
                  />
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 pt-4 mt-6 border-t border-[#2e2720] shrink-0">
                <button
                  type="button"
                  onClick={() => setModalOpen(false)}
                  className="px-4 py-2 border border-[#2e2720] font-mono rounded-xl font-bold text-[11px] uppercase tracking-wider text-stone-400 hover:bg-[#201c17] transition active:scale-95"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-amber-500 font-mono hover:bg-amber-400 text-stone-950 font-bold text-[11px] uppercase tracking-wider rounded-xl shadow transition active:scale-95"
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </NewBookModal>
      )}

      {/* Delete Modal */}
      {deleteModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-in fade-in duration-200">
          <div
            className="absolute inset-0"
            onClick={() => setDeleteModalOpen(false)}
          />
          <div className="relative w-96 rounded-2xl bg-[#1a1613] p-6 text-stone-200 shadow-2xl border border-[#2e2720] z-10 animate-in zoom-in-95 duration-150">
            <div className="flex flex-col items-center text-center mt-2 mb-6">
              <div className="flex items-center justify-center w-12 h-12 rounded-full bg-rose-500/10 border border-rose-500/20 text-rose-400 mb-4">
                <Trash2 className="w-6 h-6" />
              </div>

              <h2 className="text-lg font-bold text-stone-100">
                Delete Catalog Resource?
              </h2>

              <p className="text-xs text-stone-400 mt-2 px-2 leading-relaxed">
                You are about to permanently remove{" "}
                <span className="font-semibold text-stone-200">
                  "{formData.title || "this asset"}"
                </span>{" "}
                from the library catalog. This action cannot be reversed.
              </p>
            </div>

            <div className="flex items-center gap-2.5 pt-4 border-t border-[#2e2720]">
              <button
                type="button"
                onClick={() => setDeleteModalOpen(false)}
                className="flex-1 px-4 py-2.5 border border-[#2e2720] rounded-xl font-semibold text-xs text-stone-300 hover:bg-[#201c17] transition active:scale-95"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteBook}
                className="flex-1 px-4 py-2.5 bg-rose-600 hover:bg-rose-500 text-white rounded-xl font-semibold text-xs shadow transition active:scale-95"
              >
                Delete Asset
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Process Return Modal */}
      <ReturnModal
        open={Boolean(returnLoan)}
        loan={returnLoan}
        onClose={() => setReturnLoan(null)}
        onReturnSuccess={async (loan) => {
          try {
            await api.post(`/loans/${loan.id}/return/`);
            setLoanHistory((prev) =>
              prev.map((l) =>
                l.id === loan.id
                  ? {
                      ...l,
                      returned_date: new Date().toISOString(),
                      status: "returned",
                    }
                  : l
              )
            );
            toast.success(
              `Book "${loan.book?.title || loan.title}" returned successfully`
            );
            setReturnLoan(null);
          } catch (err) {
            console.error(err);
            toast.error("Failed to return book");
          }
        }}
      />
    </AppLayout>
  );
}